import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { 
  Building2, 
  Database, 
  RefreshCw, 
  Settings, 
  CheckCircle, 
  AlertTriangle,
  Clock,
  FileText,
  Download,
  Upload,
  Users,
  Shield,
  Plug
} from "lucide-react";
import { toast } from "sonner";

interface TylerTechConfig {
  enabled: boolean;
  serverUrl: string;
  databaseConnection: string;
  apiEndpoint: string;
  username: string;
  password: string;
  syncInterval: string;
  lastSync: string;
  totalEmployees: number;
  syncStatus: 'idle' | 'syncing' | 'success' | 'error';
  modules: {
    employeeAccess: boolean;
    payroll: boolean;
    benefits: boolean;
    performance: boolean;
  };
}

export default function TylerTechIntegration() {
  const [config, setConfig] = useState<TylerTechConfig>({
    enabled: false,
    serverUrl: '',
    databaseConnection: '',
    apiEndpoint: 'https://your-district.tylertech.com/api/v2',
    username: '',
    password: '',
    syncInterval: '24',
    lastSync: 'Never',
    totalEmployees: 0,
    syncStatus: 'idle',
    modules: {
      employeeAccess: true,
      payroll: false,
      benefits: false,
      performance: true
    }
  });

  const [syncProgress, setSyncProgress] = useState(0);
  const [fieldMappings, setFieldMappings] = useState({
    employeeId: 'Employee.EmployeeID',
    personnelNumber: 'Employee.PersonnelNumber',
    firstName: 'Employee.FirstName', 
    lastName: 'Employee.LastName',
    email: 'Employee.WorkEmail',
    department: 'Employee.Department.Name',
    position: 'Employee.Position.Title',
    hireDate: 'Employee.HireDate',
    jobTitle: 'Employee.JobTitle',
    workLocation: 'Employee.WorkLocation.Name',
    supervisorId: 'Employee.SupervisorID',
    employeeType: 'Employee.EmployeeType',
    certifications: 'Employee.Certifications',
    professionalDevelopment: 'Employee.ProfessionalDevelopment'
  });

  const handleSyncNow = async () => {
    setConfig(prev => ({ ...prev, syncStatus: 'syncing' }));
    setSyncProgress(0);
    
    const interval = setInterval(() => {
      setSyncProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setConfig(current => ({
            ...current,
            syncStatus: 'success',
            lastSync: new Date().toLocaleString(),
            totalEmployees: 1456
          }));
          toast.success('Tyler Tech Employee Access sync completed successfully');
          return 100;
        }
        return prev + 8;
      });
    }, 300);
  };

  const testConnection = async () => {
    toast.info('Testing connection to Tyler Tech Employee Access...');
    setTimeout(() => {
      toast.success('Successfully connected to Tyler Tech Employee Access');
    }, 2500);
  };

  const updateConfig = (updates: Partial<TylerTechConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  const updateFieldMapping = (field: string, value: string) => {
    setFieldMappings(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Building2 className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl font-bold text-foreground">Tyler Tech Integration</h1>
          <p className="text-muted-foreground">Connect with Tyler Tech School ERP Pro Employee Access system</p>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="configuration">Configuration</TabsTrigger>
          <TabsTrigger value="field-mapping">Field Mapping</TabsTrigger>
          <TabsTrigger value="sync-logs">Sync Logs</TabsTrigger>
          <TabsTrigger value="advanced">Advanced</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-gradient-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Tyler Tech Employee Access
                </CardTitle>
                <CardDescription>
                  Integration with Tyler Tech's comprehensive HR management system
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Integration Status</span>
                  <Badge variant={config.enabled ? "secondary" : "outline"}>
                    {config.enabled ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Last Sync</span>
                  <span className="text-sm text-muted-foreground">{config.lastSync}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Total Employees</span>
                  <span className="text-sm font-semibold">{config.totalEmployees.toLocaleString()}</span>
                </div>
                <Switch
                  checked={config.enabled}
                  onCheckedChange={(enabled) => updateConfig({ enabled })}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Key Features</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  "Employee profile synchronization",
                  "Professional development tracking",
                  "Certification and credential management",
                  "Department and position mapping",
                  "Supervisor hierarchy sync",
                  "Automated role assignments"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-success" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <Alert>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              <strong>Security Note:</strong> Tyler Tech integration uses secure API connections with encrypted data transmission. All employee data is handled according to FERPA and district privacy policies.
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle>Integration Benefits</CardTitle>
              <CardDescription>
                How Tyler Tech integration enhances your professional development ecosystem
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                    <Users className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-semibold">Unified Staff Data</h3>
                  <p className="text-sm text-muted-foreground">
                    Automatically sync all employee information including certifications and PD history
                  </p>
                </div>
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                    <RefreshCw className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-semibold">Real-time Updates</h3>
                  <p className="text-sm text-muted-foreground">
                    Keep employee records current with automated synchronization
                  </p>
                </div>
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                    <Shield className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-semibold">Compliance Ready</h3>
                  <p className="text-sm text-muted-foreground">
                    Meet state and federal reporting requirements with accurate data
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="configuration" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Connection Configuration
              </CardTitle>
              <CardDescription>
                Configure your Tyler Tech Employee Access system connection
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="server-url">Tyler Tech Server URL</Label>
                  <Input
                    id="server-url"
                    value={config.serverUrl}
                    onChange={(e) => updateConfig({ serverUrl: e.target.value })}
                    placeholder="https://your-district.tylertech.com"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Your district's Tyler Tech server URL
                  </p>
                </div>
                <div>
                  <Label htmlFor="api-endpoint">API Endpoint</Label>
                  <Input
                    id="api-endpoint"
                    value={config.apiEndpoint}
                    onChange={(e) => updateConfig({ apiEndpoint: e.target.value })}
                    placeholder="https://your-district.tylertech.com/api/v2"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Tyler Tech API endpoint URL
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="username">API Username</Label>
                  <Input
                    id="username"
                    value={config.username}
                    onChange={(e) => updateConfig({ username: e.target.value })}
                    placeholder="API service account username"
                  />
                </div>
                <div>
                  <Label htmlFor="password">API Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={config.password}
                    onChange={(e) => updateConfig({ password: e.target.value })}
                    placeholder="API service account password"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="database-connection">Database Connection String (Optional)</Label>
                <Input
                  id="database-connection"
                  value={config.databaseConnection}
                  onChange={(e) => updateConfig({ databaseConnection: e.target.value })}
                  placeholder="Server=tyler-db;Database=SchoolERP;Integrated Security=true"
                  type="password"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Direct database connection for advanced integrations
                </p>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Module Configuration</h4>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(config.modules).map(([module, enabled]) => (
                    <div key={module} className="flex items-center justify-between p-3 border rounded">
                      <div>
                        <span className="font-medium capitalize">{module.replace(/([A-Z])/g, ' $1').trim()}</span>
                        <p className="text-xs text-muted-foreground">
                          {module === 'employeeAccess' && 'Core employee information and access'}
                          {module === 'payroll' && 'Payroll and compensation data'}
                          {module === 'benefits' && 'Benefits enrollment and status'}
                          {module === 'performance' && 'Performance evaluations and goals'}
                        </p>
                      </div>
                      <Switch
                        checked={enabled}
                        onCheckedChange={(checked) => updateConfig({
                          modules: { ...config.modules, [module]: checked }
                        })}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label>Sync Interval</Label>
                <Select
                  value={config.syncInterval}
                  onValueChange={(value) => updateConfig({ syncInterval: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Every Hour</SelectItem>
                    <SelectItem value="6">Every 6 Hours</SelectItem>
                    <SelectItem value="12">Every 12 Hours</SelectItem>
                    <SelectItem value="24">Daily</SelectItem>
                    <SelectItem value="168">Weekly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3">
                <Button onClick={testConnection} variant="outline">
                  Test Connection
                </Button>
                <Button onClick={handleSyncNow} disabled={config.syncStatus === 'syncing'}>
                  {config.syncStatus === 'syncing' ? 'Syncing...' : 'Sync Now'}
                </Button>
                <Button>
                  Save Configuration
                </Button>
              </div>

              {config.syncStatus === 'syncing' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Sync Progress</span>
                    <span>{syncProgress}%</span>
                  </div>
                  <Progress value={syncProgress} className="w-full" />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="field-mapping" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Tyler Tech Field Mapping</CardTitle>
              <CardDescription>
                Map Tyler Tech Employee Access fields to Edugogy user attributes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Field mappings determine how data flows from Tyler Tech into Edugogy. Ensure field names match your Tyler Tech schema exactly.
                </AlertDescription>
              </Alert>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(fieldMappings).map(([fieldKey, fieldValue]) => (
                  <div key={fieldKey}>
                    <Label htmlFor={`field-${fieldKey}`} className="capitalize">
                      {fieldKey.replace(/([A-Z])/g, ' $1').trim()}
                    </Label>
                    <Input
                      id={`field-${fieldKey}`}
                      value={fieldValue}
                      onChange={(e) => updateFieldMapping(fieldKey, e.target.value)}
                      placeholder={`Tyler Tech field path`}
                    />
                    {fieldKey === 'certifications' && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Array field - will import all certifications and expiration dates
                      </p>
                    )}
                    {fieldKey === 'professionalDevelopment' && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Professional development history and completed training records
                      </p>
                    )}
                  </div>
                ))}
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Advanced Mapping Options</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Role Assignment Logic</Label>
                    <Select defaultValue="position-based">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="position-based">Based on Position Title</SelectItem>
                        <SelectItem value="department-based">Based on Department</SelectItem>
                        <SelectItem value="supervisor-based">Based on Supervisor Role</SelectItem>
                        <SelectItem value="custom-field">Custom Field Mapping</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Data Validation Level</Label>
                    <Select defaultValue="strict">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="strict">Strict - Require all fields</SelectItem>
                        <SelectItem value="moderate">Moderate - Allow some nulls</SelectItem>
                        <SelectItem value="flexible">Flexible - Skip validation errors</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <Button>Save Field Mappings</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sync-logs" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Tyler Tech Synchronization Logs
              </CardTitle>
              <CardDescription>
                Monitor Tyler Tech integration activity and troubleshoot issues
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { 
                    time: '2024-01-15 14:30:00', 
                    action: 'Full Employee Sync Completed', 
                    status: 'success',
                    details: '1,456 employees processed, 23 new, 15 updated, 2 certifications added'
                  },
                  { 
                    time: '2024-01-15 08:15:00', 
                    action: 'Professional Development History Sync', 
                    status: 'success',
                    details: '342 PD records imported, 89 certificates updated'
                  },
                  { 
                    time: '2024-01-14 14:22:00', 
                    action: 'Department Structure Update', 
                    status: 'warning',
                    details: 'New department "STEM Academy" detected, requires manual mapping'
                  },
                  { 
                    time: '2024-01-14 02:45:00', 
                    action: 'API Connection Test', 
                    status: 'success',
                    details: 'Connection successful, response time: 1.2s'
                  },
                  { 
                    time: '2024-01-13 14:10:00', 
                    action: 'Certification Expiration Sync', 
                    status: 'warning',
                    details: '15 certifications expiring within 60 days detected'
                  },
                  { 
                    time: '2024-01-13 08:30:00', 
                    action: 'Employee Access Rights Update', 
                    status: 'error',
                    details: 'API rate limit exceeded, sync postponed for 30 minutes'
                  }
                ].map((log, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded">
                    <div className="flex items-center gap-3">
                      {log.status === 'success' && <CheckCircle className="h-4 w-4 text-success" />}
                      {log.status === 'warning' && <AlertTriangle className="h-4 w-4 text-warning" />}
                      {log.status === 'error' && <AlertTriangle className="h-4 w-4 text-destructive" />}
                      <div>
                        <p className="text-sm font-medium">{log.action}</p>
                        <p className="text-xs text-muted-foreground">{log.details}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {log.time}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-center mt-4 pt-4 border-t">
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Export Logs
                </Button>
                <Button variant="outline" size="sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="advanced" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Data Import/Export
                </CardTitle>
                <CardDescription>
                  Advanced data management operations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Export Format</Label>
                  <Select defaultValue="csv">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="csv">CSV</SelectItem>
                      <SelectItem value="xlsx">Excel (XLSX)</SelectItem>
                      <SelectItem value="json">JSON</SelectItem>
                      <SelectItem value="xml">XML</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Include Data Types</Label>
                  {['Employee Profiles', 'Certifications', 'PD History', 'Performance Data'].map((type) => (
                    <div key={type} className="flex items-center space-x-2">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <Label className="text-sm">{type}</Label>
                    </div>
                  ))}
                </div>
                <Button className="w-full">
                  Generate Export
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Custom Queries
                </CardTitle>
                <CardDescription>
                  Execute custom Tyler Tech database queries
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Query Template</Label>
                  <Select defaultValue="active-employees">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active-employees">Active Employees</SelectItem>
                      <SelectItem value="expiring-certs">Expiring Certifications</SelectItem>
                      <SelectItem value="new-hires">New Hires (Last 30 Days)</SelectItem>
                      <SelectItem value="pd-compliance">PD Compliance Report</SelectItem>
                      <SelectItem value="custom">Custom Query</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Custom queries require database administrator permissions and should be tested in a development environment first.
                  </AlertDescription>
                </Alert>
                <Button className="w-full" variant="outline">
                  Execute Query
                </Button>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Integration Health Monitor</CardTitle>
              <CardDescription>
                Real-time monitoring of Tyler Tech integration performance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="text-center p-4 border rounded">
                  <div className="text-2xl font-bold text-success">99.8%</div>
                  <div className="text-sm text-muted-foreground">Uptime</div>
                </div>
                <div className="text-center p-4 border rounded">
                  <div className="text-2xl font-bold text-primary">1.2s</div>
                  <div className="text-sm text-muted-foreground">Avg Response</div>
                </div>
                <div className="text-center p-4 border rounded">
                  <div className="text-2xl font-bold text-success">1,456</div>
                  <div className="text-sm text-muted-foreground">Records Synced</div>
                </div>
                <div className="text-center p-4 border rounded">
                  <div className="text-2xl font-bold text-warning">3</div>
                  <div className="text-sm text-muted-foreground">Warnings</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}